import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Grid
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Check as CheckIcon,
  PlayArrow as StartIcon,
  Close as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Activity } from '../types/Activity';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ActivityListProps {
  activities: Activity[];
  onEdit: (activity: Activity) => void;
  onDelete: (id: number) => void;
  onComplete: (id: number) => void;
  onStart: (id: number) => void;
  onCancel: (id: number) => void;
  loading?: boolean;
}

const ActivityList: React.FC<ActivityListProps> = ({
  activities,
  onEdit,
  onDelete,
  onComplete,
  onStart,
  onCancel,
  loading = false
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, activity: Activity) => {
    setAnchorEl(event.currentTarget);
    setSelectedActivity(activity);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActivity(null);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#757575';
      default: return '#757575';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#388e3c';
      case 'in_progress': return '#1976d2';
      case 'cancelled': return '#9e9e9e';
      case 'pending': return '#f57c00';
      default: return '#757575';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'call': return <PhoneIcon fontSize="small" />;
      case 'email': return <EmailIcon fontSize="small" />;
      case 'meeting': return <EventIcon fontSize="small" />;
      default: return <EventIcon fontSize="small" />;
    }
  };

  // Função auxiliar para formatar datas com segurança
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'Não definida';
    
    try {
      const date = new Date(dateString);
      // Verifica se a data é válida
      if (isNaN(date.getTime())) {
        return 'Data inválida';
      }
      return format(date, 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return 'Data inválida';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>Carregando atividades...</Typography>
      </Box>
    );
  }

  if (activities.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <Typography color="text.secondary">
          Nenhuma atividade encontrada
        </Typography>
      </Box>
    );
  }

    return (
      <Grid container spacing={2} key="activities-container">
        {activities.map((activity) => (
          <Grid item xs={12} md={6} lg={4} key={`activity-${activity.id}`}>
          <Card
            sx={{
              height: '100%',
              borderLeft: `4px solid ${activity.color}`,
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}
          >
            <CardContent>
              {/* Header */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ color: activity.color }}>
                    {getTypeIcon(activity.activity_type)}
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {activity.activity_type_display}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuClick(e, activity)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* Title */}
              <Typography variant="h6" gutterBottom noWrap>
                {activity.title}
              </Typography>

              {/* Description */}
              {activity.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {activity.description}
                </Typography>
              )}

              {/* Chips */}
              <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                <Chip
                  label={activity.priority_display}
                  size="small"
                  sx={{
                    backgroundColor: `${getPriorityColor(activity.priority)}15`,
                    color: getPriorityColor(activity.priority),
                    fontWeight: 'medium'
                  }}
                />
                <Chip
                  label={activity.status_display}
                  size="small"
                  sx={{
                    backgroundColor: `${getStatusColor(activity.status)}15`,
                    color: getStatusColor(activity.status),
                    fontWeight: 'medium'
                  }}
                />
                {activity.is_overdue && activity.status !== 'completed' && (
                  <Chip
                    label="Atrasada"
                    size="small"
                    color="error"
                    sx={{ fontWeight: 'bold' }}
                  />
                )}
              </Box>

              {/* Dates */}
              <Box display="flex" flexDirection="column" gap={0.5}>
                {activity.follow_up_date && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Para:</strong> {formatDateTime(activity.follow_up_date)}
                  </Typography>
                )}
                {activity.completed_at && (
                  <Typography variant="body2" color="success.main">
                    <strong>Concluída:</strong> {formatDateTime(activity.completed_at)}
                  </Typography>
                )}
              </Box>

              {/* Assignee */}
              {activity.assigned_to_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  <strong>Responsável:</strong> {activity.assigned_to_name}
                </Typography>
              )}

              {/* Lead */}
              {activity.lead_name && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Lead:</strong> {activity.lead_name}
                </Typography>
              )}

              {/* Actions */}
              {activity.status === 'pending' && (
                <Box display="flex" gap={1} mt={2}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<StartIcon />}
                    onClick={() => onStart(activity.id)}
                    color="primary"
                    fullWidth
                  >
                    Iniciar
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CheckIcon />}
                    onClick={() => onComplete(activity.id)}
                    color="success"
                  >
                    Concluir
                  </Button>
                </Box>
              )}

              {activity.status === 'in_progress' && (
                <Box display="flex" gap={1} mt={2}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={() => onComplete(activity.id)}
                    color="success"
                    fullWidth
                  >
                    Concluir
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => onCancel(activity.id)}
                    color="error"
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedActivity && (
          <>
            <MenuItem
              key="edit"
              onClick={() => {
                onEdit(selectedActivity);
                handleMenuClose();
              }}
            >
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Editar
            </MenuItem>
            <MenuItem
              key="complete"
              onClick={() => {
                if (selectedActivity.status !== 'completed') {
                  onComplete(selectedActivity.id);
                  handleMenuClose();
                }
              }}
              disabled={selectedActivity.status === 'completed'}
            >
              <CheckIcon fontSize="small" sx={{ mr: 1 }} />
              Concluir
            </MenuItem>
            <MenuItem
              key="delete"
              onClick={() => {
                onDelete(selectedActivity.id);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
              Excluir
            </MenuItem>
          </>
        )}
      </Menu>
    </Grid>
  );
};

export default ActivityList;
